import { TEventStationDetails, TRemoteDeploymentCheck, TSyncEventStatus, TVendorDTO } from '@monorepo/types';
import {
  TBctxItemWithoutSize,
  TBgEventsSyncingDBLockDetails,
  TRemoteDeploymentCheckWithEventsSyncResult,
  TUserActualsDBLockDetails,
} from '../common/api.types';
import { TRESPONSE_WashReceiveTransactionListingResponse } from './bulk-washing-receive.api.types';
import {
  TREQUEST_WashSendTransactionUndo,
  TRESPONSE_VendorsResponse,
  TRESPONSE_WashSendTransactionListingResponse,
  TSizeQuantity,
} from './bulk-washing-send.api.types';

export type TBulkCountSizeMapItemType = 'ACTUAL' | 'WIP' | 'BG_ACTUAL' | 'BG_WIP' | 'SAVED_COUNT';

export type TBulkGradingPageSizeItem = {
  name: string;
  wipQty: number;
  activeQty: number;
  sentQty: number;
  receiveQty: number;
};

export type TBulkGradingPageSizeItemVM = {
  savedData: TBulkGradingPageSizeItem;
  currentCount: number;
};

export type TSizeToUserDBActualMapItem = {
  name: string;
  totalCompletedCount: number;
  sentQty: number;
};

export type TSizeToUserDBWipMapItem = {
  name: string;
  wipQty: number;
};

export type TBgSizeToActualMapItem = {
  name: string;
  actuals: number;
};

export type TBgSizeToWipMapItem = {
  name: string;
  wipQty: number;
};

export type TSizeToSavedCountMap = {
  name: string;
  savedCount: number;
};

type TBulkCountAppSizeMapItem =
  | TSizeToUserDBWipMapItem
  | TSizeToUserDBActualMapItem
  | TBgSizeToActualMapItem
  | TBgSizeToWipMapItem
  | TSizeToSavedCountMap;

export type TBulkCountAppSizeMap = {
  [size: string]: TBulkCountAppSizeMapItem;
};

export type TBctxAndVendorWipMapItem = {
  bctxItemListWithoutSize: TBctxItemWithoutSize[]; // TODO: It should be always empty in case of TShiftMapItem for memory or we can create two type of TBctxAndVendorWipMapItem
  sizeMap: Record<string, TSizeToUserDBWipMapItem>;
  bctxKeyAndVendorId: string;
};

export type TBctxAndVendorWipMap = {
  [bctxKeyAndVendorId: string]: TBctxAndVendorWipMapItem;
};

export type TBulkCountUserDBShiftSizeMapItem = {
  sizeMap: Record<string, TSizeToUserDBActualMapItem>;
  bctxKeyAndVendorId: string;
};

export type TBulkCountUserDBShiftMapItem = {
  [bctxKeyAndVendorId: string]: TBulkCountUserDBShiftSizeMapItem;
};

export type TBulkCountBgDBShiftMapItem = {
  [bctxKeyAndVendorId: string]: {
    sizeMap: Record<string, TBgSizeToActualMapItem>;
    bctxKeyAndVendorId: string;
    bctxItemListWithoutSize: TBctxItemWithoutSize[];
  };
};

export type TBulkCountUserDBShiftMap = {
  [shift_id: string]: TBulkCountUserDBShiftMapItem;
};

export type TBulkCountBgDBShiftMap = {
  [shift_id: string]: TBulkCountBgDBShiftMapItem;
};

export type TBulkCountUserDBLineMapItem = {
  bctxAndVendorWipMap: TBctxAndVendorWipMap;
  shiftMap: TBulkCountUserDBShiftMap;
};

export type TBulkCountBgDBLineMapItem = {
  bctxAndVendorWipMap: TBctxAndVendorWipMap;
  shiftMap: TBulkCountBgDBShiftMap;
};

export type TBulkCountUserDBLineMap = {
  [lineId: string]: TBulkCountUserDBLineMapItem;
};

export type TBulkCountBgDBLineMap = {
  [lineId: string]: TBulkCountBgDBLineMapItem;
};

/** bulk event types */

export type TBulkCountSyncEventsDTO = {
  events: TBulkCountAppEventWithStatus[];
  failedEventsIndices: number[];
  lastBatchSyncStatus: TSyncEventStatus;
};

type TBulkCountEventMetadata = {
  eventTime: number;
  stationDetails: TEventStationDetails;
  bctxWithoutSize: TBctxItemWithoutSize[];
  bctxKey: string;
  vendorId: TVendorDTO['vendorId'];
};

export type TWashingUndoTransactionDetail = Pick<TREQUEST_WashSendTransactionUndo, 'transactionId'> & {
  sizeQuantities: TSizeQuantity[];
  bctxWithoutSize: TBctxItemWithoutSize[];
  vendorId?: TVendorDTO['vendorId'];
};

export type TBulkCountUserAction = {
  chainWithInitForLineChange?: TBulkWashingEventType;
  chainWithAppUpdate?: TRemoteDeploymentCheck;
  reconcileAndShowAppUpdateError?: TRemoteDeploymentCheckWithEventsSyncResult;
  chainWithUndoWashingEvent?: TWashingUndoTransactionDetail & {
    eventType: TBulkWashingEventType;
    startTime: number;
    endTime: number;
  };
};

export type TBulkWashingEventType = 'SEND' | 'RECEIVE';

export type TBulkWashingSendEvent = {
  type: 'SEND';
  metaData: TBulkCountEventMetadata;
  sizeQuantities: TSizeQuantity[];
};

export type TBulkWashingReceiveEvent = {
  type: 'RECEIVE';
  metaData: TBulkCountEventMetadata;
  sizeQuantities: TSizeQuantity[];
};

export type TBulkCountAppEventWithoutStatus = TBulkWashingSendEvent | TBulkWashingReceiveEvent;

export type TBulkCountAppEventWithStatus = {
  event: TBulkCountAppEventWithoutStatus;
  backgroundMetadata: TBulkEventBackgroundMetadata;
};

type TYET_TO_BE_SYNCED = 'YET_TO_BE_SYNCED';
type TSYNCED = 'SYNCED';
type TSERVER_ERROR = 'SERVER_ERROR';
type TVALIDATION_ERROR = 'VALIDATION_ERROR';

type TBulkEventBackgroundMetadata = {
  remainingRetriesCount: number;
  eventStatus: TYET_TO_BE_SYNCED | TSYNCED | TSERVER_ERROR | TVALIDATION_ERROR;
  errorDetails?: any;
};

export const DEFAULT_VENDOR: TVendorDTO = {
  vendorId: 1, //TO be filled from config
  vendorName: '',
  vendorType: 'NOMINATED',
  location: '',
  plant: 'EMBROIDERY',
  department: 'FINISHING',
  status: 'ACTIVE',
};

/** user actuals DB types */
export type TBulkCountAppUserActualsDB = {
  dailyCache: {
    lastStyleSyncInfoTimestamp: number | undefined;
    nextCacheResetTimestamp: number;
    totalCount: number;
    lineMap: TBulkCountUserDBLineMap;
    events: {
      list: TBulkCountAppEventWithoutStatus[];
    };
  };
  persistanceCache: {
    pendingTransactions: TBulkCountPendingTransactions;
  };
};

/** user background DB types */

export type TBulkCountAppBackgroundDB = {
  dailyCache: {
    nextCacheResetTimestamp: number;
    totalCount: number;
    lineMap: TBulkCountBgDBLineMap;
    events: {
      list: TBulkEventBackgroundMetadata[];
    };
  };
  persistanceCache: {
    pastDaysList: TBulkCountAppEventWithStatus[];
  };
};

/** bulk washing transactions types  */

type TBulkCountPendingTransactionsItem = {
  lineId: string;
  shiftId: string;
  bctxKey: string;
  timeStamp: number;
  vendorId: TVendorDTO['vendorId'] | undefined;
  sizeMap: Record<string, TSizeToSavedCountMap>;
};

export type TBulkCountPendingTransactionWithBctxList = {
  bcxtList: TBctxItemWithoutSize[];
  transaction: TBulkCountPendingTransactionsItem;
  vendor?: TVendorDTO;
};

export type TBulkCountPendingTransactions = TBulkCountPendingTransactionsItem[];

type TBulkWashingSendSyncedTransactions = {
  type: 'SEND';
  res: TRESPONSE_WashSendTransactionListingResponse;
};

type TBulkWashingReceiveSyncedTransactions = {
  type: 'RECEIVE';
  res: TRESPONSE_WashReceiveTransactionListingResponse;
};

export type TBulkWashingSyncedTransactions = TBulkWashingSendSyncedTransactions | TBulkWashingReceiveSyncedTransactions;

/** BULK COUNT APP DB STORE TYPES */

export type TBulkCountAppDBStore = {
  vendors: TRESPONSE_VendorsResponse;
  userDBLockDetails: TUserActualsDBLockDetails;
  userDB: TBulkCountAppUserActualsDB;
  bgDBLockDetails: TBgEventsSyncingDBLockDetails;
  bgDB: TBulkCountAppBackgroundDB;
  transactions: TBulkWashingSyncedTransactions;
};

export type TInitActions = {
  chainWithSyncBctx?: boolean;
  chainWithSyncVendors?: boolean;
  chainWithSyncCount?: boolean;
  chainWithSyncBctxWiseCount?: boolean;
  chainWithSyncBctxWiseWipe?: boolean;
  chainWithVendorToBctxDataCombinations?: boolean;
};

export type TBulkCountAppDBStoreChainActions = {
  initActions: TInitActions;
};

export type TBctxAndVendorKeyToBctxWithoutSizeAndSizeListMapItem = {
  bctxItemListWithoutSize: TBctxItemWithoutSize[];
  sizes: string[];
};

export type TBctxAndVendorKeyToBctxWithoutSizeAndSizeListMap = {
  [bctxKeyAndVendorId: string]: TBctxAndVendorKeyToBctxWithoutSizeAndSizeListMapItem;
};

export type TBulkCountClientDBInitInfo = {
  isInit: boolean;
  isReload: boolean;
  isDayEnd: boolean;
  userDB: TBulkCountAppUserActualsDB;
  bgDB: TBulkCountAppBackgroundDB;
};
